import { nectarApi } from '@/features/nectar/api/client'
import { parseMoney } from '@/utils'
import { type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import type { Enrollment, EnrollmentStatus, EnrollmentsEnvelope } from './models'

type EnrollmentWire = Omit<Enrollment, 'pendingUsd' | 'earnedUsd'> & {
  pendingUsd: string
  earnedUsd: string
}

type EnrollmentsEnvelopeWire = {
  data: EnrollmentWire[]
  meta: EnrollmentsEnvelope['meta']
}

type UseFetchEnrollmentsArgs = {
  limit: number
  statuses?: EnrollmentStatus[]
}

const buildParams = (page: number, limit: number, statuses?: EnrollmentStatus[]): URLSearchParams => {
  // Use URLSearchParams so multiple statuses serialize as repeated `status=`
  // params (e.g. `?status=A&status=B`). Axios' default array serializer would
  // produce `status[]=A&status[]=B`, which the backend rejects.
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('limit', String(limit))
  if (statuses && statuses.length > 0) {
    for (const status of statuses) {
      params.append('status', status)
    }
  }
  return params
}

export const useFetchEnrollments = ({
  limit,
  statuses
}: UseFetchEnrollmentsArgs): UseInfiniteQueryResult<InfiniteData<EnrollmentsEnvelope>> => {
  const sortedStatuses = statuses ? [...statuses].sort() : undefined
  return useInfiniteQuery({
    queryKey: ['enrollments', { limit, statuses: sortedStatuses }],
    queryFn: async ({ pageParam }): Promise<EnrollmentsEnvelope> => {
      try {
        const wire = await nectarApi.get<EnrollmentsEnvelopeWire>(
          '/v1/enrollments',
          buildParams(pageParam, limit, statuses)
        )
        return {
          data: wire.data.map((row) => ({
            ...row,
            pendingUsd: parseMoney(row.pendingUsd),
            earnedUsd: parseMoney(row.earnedUsd)
          })),
          meta: wire.meta
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return { data: [], meta: { total: 0, page: pageParam, limit } }
        }
        throw err
      }
    },
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.total > last.meta.page * last.meta.limit ? last.meta.page + 1 : undefined),
    staleTime: 30_000
  })
}
